import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Edicion } from '@/config/ediciones';
import { type Tarea, type PlanningRepo, repoLocal, sembrarSiVacio, addDias } from '@/planning';
import { getHerramienta, enlaceHerramienta } from '@/tools/registro';

/**
 * Planning de cobertura — UI (arriba del panel).
 *
 * Calendario de dos semanas (la del campeonato y la siguiente) con cajas de día
 * de ancho fijo. Al pulsar una tarea, su editor/consultor aparece DEBAJO del
 * calendario, a dos columnas para no ocupar mucha altura. Dependencias con
 * alerta (una tarea puede requerir otra previa, p. ej. la carga de datos).
 *
 * Habla solo con `PlanningRepo`; por defecto, repo local provisional (no
 * compartido todavía).
 */

/** Equipo para responsable y colaboradores. */
const EQUIPO = ['Santi', 'Ricky', 'Mateo', 'Pablo', 'Otro'];

/** Ancho mínimo de cada caja de día (px). Las cajas estiran para llenar el
 *  ancho disponible; por debajo de este mínimo, el calendario hace scroll. */
const ANCHO_DIA = 140;

/** Lunes (ISO) de la semana que contiene la fecha dada. */
function lunesDe(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  const dow = (d.getDay() + 6) % 7; // 0 = lunes
  return addDias(iso, -dow);
}

function diaCorto(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'long' });
}
function numeroDia(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric' });
}
function fechaLarga(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

const HOY: string = (() => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
})();

export function PlanningCobertura({
  edicion,
  repo = repoLocal,
  sinCabecera,
}: {
  edicion: Edicion;
  repo?: PlanningRepo;
  /** true = el título/píldora los pone quien lo envuelve (la sección plegable
   *  del Panel, 2026-07-21) y aquí no se pintan, para no duplicarlos. */
  sinCabecera?: boolean;
}) {
  const [tareas, setTareas] = useState<Tarea[]>([]);
  const [cargando, setCargando] = useState(true);
  const [selId, setSelId] = useState<string | null>(null);

  // Borrador de edición del detalle (se rellena al seleccionar una tarea).
  const [eTexto, setETexto] = useState('');
  const [eDesc, setEDesc] = useState('');
  const [eResp, setEResp] = useState('');
  const [eColab, setEColab] = useState<string[]>([]);
  const [eDeps, setEDeps] = useState<string[]>([]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      setCargando(true);
      await sembrarSiVacio(repo, edicion);
      const t = await repo.listar(edicion.id);
      if (vivo) {
        setTareas(t);
        setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [repo, edicion]);

  const dias = useMemo(() => {
    const l = lunesDe(edicion.fechaInicio);
    return Array.from({ length: 14 }, (_, i) => addDias(l, i));
  }, [edicion.fechaInicio]);

  const tareasById = useMemo(() => {
    const m = new Map<string, Tarea>();
    for (const t of tareas) m.set(t.id, t);
    return m;
  }, [tareas]);

  const porDia = useMemo(() => {
    const m = new Map<string, Tarea[]>();
    for (const t of tareas) {
      const arr = m.get(t.fecha) ?? [];
      arr.push(t);
      m.set(t.fecha, arr);
    }
    return m;
  }, [tareas]);

  const esDiaCampeonato = useCallback(
    (iso: string) => iso >= edicion.fechaInicio && iso <= edicion.fechaFin,
    [edicion.fechaInicio, edicion.fechaFin],
  );

  /** Dependencias sin cumplir (tareas de las que depende y aún no están hechas). */
  const bloqueoDe = useCallback(
    (t: Tarea): Tarea[] =>
      t.dependeDe.map((id) => tareasById.get(id)).filter((d): d is Tarea => !!d && !d.hecha),
    [tareasById],
  );

  const sel = selId ? tareasById.get(selId) ?? null : null;

  function seleccionar(t: Tarea) {
    setSelId(t.id);
    setETexto(t.texto);
    setEDesc(t.descripcion);
    setEResp(t.responsable ?? '');
    setEColab([...t.colaboradores]);
    setEDeps([...t.dependeDe]);
  }

  /** Aplica cambios en memoria (optimista) para no recargar ni perder selección. */
  function aplicar(id: string, cambios: Partial<Tarea>) {
    setTareas((prev) =>
      prev.map((t) => (t.id === id ? { ...t, ...cambios, actualizadaEn: Date.now() } : t)),
    );
  }

  async function toggle(t: Tarea) {
    aplicar(t.id, { hecha: !t.hecha });
    await repo.actualizar(t.id, { hecha: !t.hecha });
  }

  async function guardarDetalle() {
    if (!sel) return;
    const cambios: Partial<Tarea> = {
      texto: eTexto.trim() || '(sin título)',
      descripcion: eDesc,
      responsable: eResp.trim() || null,
      colaboradores: eColab,
      dependeDe: eDeps,
    };
    aplicar(sel.id, cambios);
    await repo.actualizar(sel.id, cambios);
  }

  async function borrar(t: Tarea) {
    setTareas((prev) => prev.filter((x) => x.id !== t.id));
    if (selId === t.id) setSelId(null);
    await repo.borrar(t.id);
  }

  async function anadirEn(dia: string) {
    const nueva = await repo.crear({
      edicionId: edicion.id,
      fecha: dia,
      texto: 'Nueva tarea',
      origen: 'manual',
    });
    setTareas((prev) => [...prev, nueva]);
    seleccionar(nueva);
  }

  const semanas = [dias.slice(0, 7), dias.slice(7, 14)];

  return (
    <div>
      {sinCabecera ? null : (
      <div className="ld-spread" style={{ alignItems: 'baseline', marginBottom: 12 }}>
        <h3
          style={{
            margin: 0,
            fontSize: 14,
            color: 'var(--ld-text-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
          }}
        >
          Planning de cobertura
        </h3>
        <span
          className="ld-pill ld-pill--muted"
          style={{ fontSize: 10 }}
          title="Guardado solo en este navegador hasta conectar Firestore"
        >
          Provisional · aún no compartido
        </span>
      </div>
      )}

      {cargando ? (
        <p className="muted" style={{ fontSize: 13, display: 'flex', alignItems: 'center' }}>
          <span className="ld-spinner" aria-hidden />
          Cargando planning…
        </p>
      ) : (
        <>
          {/* Calendario (ancho completo, cajas de día de ancho fijo). */}
          <div style={{ overflowX: 'auto', paddingBottom: 4 }}>
            {semanas.map((semana, i) => (
              <div key={i} style={{ marginBottom: i === 0 ? 8 : 0 }}>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(7, minmax(${ANCHO_DIA}px, 1fr))`, gap: 8 }}>
                  {semana.map((dia) => {
                    const items = porDia.get(dia) ?? [];
                    const esHoy = dia === HOY;
                    const esCamp = esDiaCampeonato(dia);
                    return (
                      <div
                        key={dia}
                        className="ld-card"
                        style={{
                          minHeight: 116,
                          padding: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          overflow: 'hidden',
                          outline: esHoy ? '2px solid var(--ld-accent, #c0842a)' : 'none',
                        }}
                      >
                        {/* Cabecera del día: resaltada si es día de campeonato. */}
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                            padding: '4px 7px',
                            background: esCamp
                              ? 'var(--ld-accent, #c0842a)'
                              : 'var(--ld-chip-bg, rgba(128,128,128,0.18))',
                            color: esCamp ? '#fff' : 'inherit',
                          }}
                        >
                          <span
                            style={{
                              fontSize: 11,
                              textTransform: 'capitalize',
                              opacity: 0.9,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              marginRight: 6,
                            }}
                          >
                            {diaCorto(dia)}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 700 }}>{numeroDia(dia)}</span>
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                            padding: 6,
                            flex: 1,
                          }}
                        >
                          {items.map((t) => {
                            const bloqueada = !t.hecha && bloqueoDe(t).length > 0;
                            const activa = selId === t.id;
                            return (
                              <button
                                key={t.id}
                                type="button"
                                onClick={() => seleccionar(t)}
                                title={t.texto}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 4,
                                  width: '100%',
                                  textAlign: 'left',
                                  padding: '3px 5px',
                                  borderRadius: 6,
                                  border: activa
                                    ? '1px solid var(--ld-accent, #c0842a)'
                                    : '1px solid var(--ld-border, rgba(128,128,128,0.35))',
                                  background: bloqueada
                                    ? 'rgba(229,72,77,0.15)'
                                    : t.tipo === 'carga-datos'
                                      ? 'rgba(192,132,42,0.20)'
                                      : 'transparent',
                                  color: 'inherit',
                                  cursor: 'pointer',
                                  fontSize: 11,
                                  lineHeight: 1.2,
                                }}
                              >
                                <span aria-hidden style={{ flexShrink: 0 }}>
                                  {t.hecha ? '✓' : bloqueada ? '⚠' : '•'}
                                </span>
                                <span
                                  style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    textDecoration: t.hecha ? 'line-through' : 'none',
                                    opacity: t.hecha ? 0.6 : 1,
                                  }}
                                >
                                  {t.texto}
                                </span>
                              </button>
                            );
                          })}

                          <button
                            type="button"
                            onClick={() => void anadirEn(dia)}
                            title="Añadir tarea"
                            style={{
                              marginTop: 'auto',
                              fontSize: 11,
                              padding: '2px 4px',
                              border: 'none',
                              background: 'transparent',
                              color: 'var(--ld-text-muted)',
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            + añadir
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Detalle debajo del calendario, a ancho completo y dos columnas. */}
          <div className="ld-card" style={{ marginTop: 14 }}>
            {!sel ? (
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>
                Haz clic en un evento del calendario para ver o editar su detalle.
              </p>
            ) : (
              <DetalleTarea
                key={sel.id}
                sel={sel}
                tareas={tareas}
                bloqueo={bloqueoDe(sel)}
                eTexto={eTexto}
                eDesc={eDesc}
                eResp={eResp}
                eColab={eColab}
                eDeps={eDeps}
                setETexto={setETexto}
                setEDesc={setEDesc}
                setEResp={setEResp}
                setEColab={setEColab}
                setEDeps={setEDeps}
                onToggle={() => void toggle(sel)}
                onGuardar={() => void guardarDetalle()}
                onBorrar={() => void borrar(sel)}
                onCerrar={() => setSelId(null)}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Campo({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="muted" style={{ margin: '0 0 3px', fontSize: 11 }}>
        {label}
      </p>
      {children}
    </div>
  );
}

function DetalleTarea({
  sel,
  tareas,
  bloqueo,
  eTexto,
  eDesc,
  eResp,
  eColab,
  eDeps,
  setETexto,
  setEDesc,
  setEResp,
  setEColab,
  setEDeps,
  onToggle,
  onGuardar,
  onBorrar,
  onCerrar,
}: {
  sel: Tarea;
  tareas: Tarea[];
  bloqueo: Tarea[];
  eTexto: string;
  eDesc: string;
  eResp: string;
  eColab: string[];
  eDeps: string[];
  setETexto: (s: string) => void;
  setEDesc: (s: string) => void;
  setEResp: (s: string) => void;
  setEColab: (v: string[]) => void;
  setEDeps: (v: string[]) => void;
  onToggle: () => void;
  onGuardar: () => void;
  onBorrar: () => void;
  onCerrar: () => void;
}) {
  const herr = sel.herramientaId ? getHerramienta(sel.herramientaId) : undefined;
  const otras = tareas.filter((t) => t.id !== sel.id);

  function toggleDep(id: string) {
    setEDeps(eDeps.includes(id) ? eDeps.filter((x) => x !== id) : [...eDeps, id]);
  }
  function toggleColab(p: string) {
    setEColab(eColab.includes(p) ? eColab.filter((x) => x !== p) : [...eColab, p]);
  }

  return (
    <div>
      {/* Cabecera + alerta a ancho completo */}
      <div className="ld-spread" style={{ alignItems: 'baseline', marginBottom: 8 }}>
        <span className="muted mono" style={{ fontSize: 11 }}>
          {fechaLarga(sel.fecha)}
        </span>
        <span className="ld-row" style={{ gap: 8, alignItems: 'center' }}>
          {sel.tipo === 'carga-datos' ? (
            <span className="ld-pill ld-pill--record" style={{ fontSize: 10 }}>
              Carga de datos
            </span>
          ) : null}
          <button
            type="button"
            className="ld-btn ld-btn--ghost ld-btn--small"
            onClick={onCerrar}
            title="Cerrar"
          >
            ×
          </button>
        </span>
      </div>

      {bloqueo.length > 0 && !sel.hecha ? (
        <div
          style={{
            fontSize: 12,
            color: '#e5484d',
            border: '1px solid rgba(229,72,77,0.5)',
            background: 'rgba(229,72,77,0.10)',
            borderRadius: 6,
            padding: '6px 8px',
            marginBottom: 10,
          }}
        >
          ⚠ Bloqueada — antes hay que hacer: {bloqueo.map((b) => b.texto).join(', ')}.
        </div>
      ) : null}

      {/* Dos columnas */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 16,
        }}
      >
        {/* Columna izquierda */}
        <div className="ld-stack" style={{ gap: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={sel.hecha} onChange={onToggle} />
            Marcar como hecha
          </label>

          <Campo label="Tarea">
            <input
              type="text"
              value={eTexto}
              onChange={(e) => setETexto(e.target.value)}
              style={{ width: '100%' }}
            />
          </Campo>

          <Campo label="Descripción">
            <textarea
              value={eDesc}
              onChange={(e) => setEDesc(e.target.value)}
              rows={4}
              style={{ width: '100%', resize: 'vertical', fontFamily: 'inherit', fontSize: 13 }}
              placeholder="Qué hay que hacer, notas…"
            />
          </Campo>

          {herr ? (
            <Campo label="Herramienta">
              {herr.estado === 'listo' && enlaceHerramienta(herr) ? (
                <Link to={enlaceHerramienta(herr) as string} className="ld-btn ld-btn--primary ld-btn--small">
                  Abrir {herr.titulo} →
                </Link>
              ) : (
                <span className="ld-pill ld-pill--muted" style={{ fontSize: 11 }}>
                  {herr.titulo} · Próximamente
                </span>
              )}
            </Campo>
          ) : null}
        </div>

        {/* Columna derecha */}
        <div className="ld-stack" style={{ gap: 10 }}>
          <Campo label="Responsable">
            <select value={eResp} onChange={(e) => setEResp(e.target.value)} style={{ width: '100%' }}>
              <option value="">Sin asignar</option>
              {EQUIPO.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
              {eResp && !EQUIPO.includes(eResp) ? <option value={eResp}>{eResp}</option> : null}
            </select>
          </Campo>

          <Campo label="Colaboradores">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {EQUIPO.map((p) => (
                <label key={p} style={{ display: 'flex', gap: 5, alignItems: 'center', fontSize: 13 }}>
                  <input type="checkbox" checked={eColab.includes(p)} onChange={() => toggleColab(p)} />
                  {p}
                </label>
              ))}
            </div>
          </Campo>

          <Campo label="Depende de (marca las tareas previas necesarias)">
            <div
              style={{
                maxHeight: 120,
                overflowY: 'auto',
                border: '1px solid var(--ld-border, rgba(128,128,128,0.35))',
                borderRadius: 6,
                padding: 6,
              }}
            >
              {otras.length === 0 ? (
                <span className="faint" style={{ fontSize: 12 }}>
                  No hay otras tareas.
                </span>
              ) : (
                otras.map((t) => (
                  <label
                    key={t.id}
                    style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, padding: '1px 0' }}
                  >
                    <input
                      type="checkbox"
                      checked={eDeps.includes(t.id)}
                      onChange={() => toggleDep(t.id)}
                    />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {t.texto} <span className="faint">· {t.fecha.slice(5)}</span>
                    </span>
                  </label>
                ))
              )}
            </div>
          </Campo>
        </div>
      </div>

      {/* Acciones a ancho completo */}
      <div className="ld-row" style={{ gap: 8, marginTop: 14 }}>
        <button type="button" className="ld-btn ld-btn--primary ld-btn--small" onClick={onGuardar}>
          Guardar
        </button>
        <button type="button" className="ld-btn ld-btn--ghost ld-btn--small" onClick={onBorrar}>
          Borrar
        </button>
      </div>
    </div>
  );
}
